import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import apiService from '../../services/api';
import { Confirm, DNS_CONFIG_LABELS, DomainTabProps, TabBody, dnsConfigOf, errorText } from './shared';

const MODE_HELP: Record<number, string> = {
  1: 'Use name servers hosted elsewhere (e.g. a web host or Microsoft 365).',
  2: 'Synergy forwards email and websites. Manage the forwards on the Forwarding tab.',
  3: 'Show Synergy\'s parked page.',
  4: 'Synergy hosts the DNS zone. Manage records on the DNS records tab.',
};

const NameserversTab: React.FC<DomainTabProps> = ({ serviceId, info, onChanged }) => {
  const current = dnsConfigOf(info);
  const [mode, setMode] = useState<number>(current ?? 1);
  const [servers, setServers] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [msg, setMsg] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setMode(current ?? 1);
    setServers((info?.name_servers || []).join('\n'));
  }, [current, info?.name_servers]);

  const switchingRecordModes = (current === 2 || current === 4) && (mode === 2 || mode === 4) && mode !== current;

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await apiService.domainRequest('post', serviceId, '/nameservers', {
        dns_config: mode,
        nameservers: mode === 1 ? servers.split(/[\s,]+/).filter(Boolean) : [],
        confirm_records_deleted: switchingRecordModes,
      });
      setConfirm(false);
      setMsg({ severity: 'success', text: 'Saved. Changes can take a while to spread across the internet.' });
      await onChanged();
    } catch (e) {
      setConfirm(false);
      setMsg({ severity: 'error', text: errorText(e, 'Could not update the name servers.') });
    } finally {
      setBusy(false);
    }
  };

  const unchanged = mode === current && (mode !== 1 || servers.split(/[\s,]+/).filter(Boolean).join(',') === (info?.name_servers || []).join(','));

  return (
    <TabBody>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 0 }}>
        <Typography variant="subtitle2" gutterBottom>How this domain's DNS is handled</Typography>
        <RadioGroup value={mode} onChange={(e) => setMode(parseInt(e.target.value, 10))}>
          {[1, 2, 3, 4].map((m) => (
            <FormControlLabel
              key={m}
              value={m}
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={m === current ? 700 : 400}>
                    {DNS_CONFIG_LABELS[m]}{m === current ? ' (current)' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{MODE_HELP[m]}</Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
          ))}
        </RadioGroup>

        {mode === 1 ? (
          <TextField
            label="Name servers (one per line, at least two)"
            value={servers}
            onChange={(e) => setServers(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            sx={{ mt: 1, maxWidth: 480 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Synergy's name servers are used: ns1.nameserver.net.au, ns2.nameserver.net.au, ns3.nameserver.net.au.
          </Typography>
        )}

        {switchingRecordModes && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Switching between email / web forwarding and DNS hosting deletes the existing {current === 4 ? 'DNS records' : 'forwards'} straight away.
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" disabled={busy || unchanged} onClick={() => (switchingRecordModes ? setConfirm(true) : save())}>
            {busy && !confirm ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      </Paper>

      <Confirm
        open={confirm}
        busy={busy}
        danger
        title="Delete existing records?"
        text={`Switching to “${DNS_CONFIG_LABELS[mode]}” deletes the current ${current === 4 ? 'DNS records' : 'email and web forwards'} straight away. This can't be undone.`}
        confirmLabel="Switch and delete"
        onCancel={() => setConfirm(false)}
        onConfirm={save}
      />
    </TabBody>
  );
};

export default NameserversTab;
